import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, reactive, watch, onMounted, Ref, ComponentPublicInstance } from 'vue';
import { useRoute } from 'vue-router';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { AxiosResponse } from 'axios';

import { useUiStore } from '@/stores';
import { goBack } from '@/composables/useNavigation';
import { ConnectFlowSurveyService, VaultService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { getAudioFormat } from '@/utils/audio';

import {
    Permission,
    FormAction,
    ConnectFlowSurveyModel,
    ConnectFlowSurveyFormState,
} from '@/types';

import { required, requiredIf, minLength, maxLength } from '@/utils/validators';
import { ConnectFlowSurveyQuestionForm } from '@/views/pages/connect-flow-survey/components';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConnectFlowSurveyFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props, { expose: __expose }) {
  __expose();

const props = __props;

const route = useRoute();
const uiStore = useUiStore();

const formState = reactive<ConnectFlowSurveyFormState>({
    name: null,
    textToAudio: null,
    audioFilePath: null,
    questions: [],
    isActive: true,
    finalTextToAudio: null,
    finalAudioFilePath: null,
});

const file = ref<File | null>(null);
const audioPreview = ref<string | null>(null);
const questionRefs = ref<Record<string, InstanceType<typeof ConnectFlowSurveyQuestionForm> | null>>(
    {},
);
const finalFile = ref<File | null>(null);
const finalAudioPreview = ref<string | null>(null);

const rules = computed<ValidationArgs<ConnectFlowSurveyFormState>>(() => ({
    name: { required, maxLength: maxLength(500) },
    textToAudio: {
        required: requiredIf(() => !formState.audioFilePath),
        maxLength: maxLength(500),
    },
    finalTextToAudio: {
        required: requiredIf(() => !formState.audioFilePath),
        maxLength: maxLength(500),
    },
    audioFilePath: {
        required: requiredIf(() => formState.textToAudio?.length === 0),
    },
    finalAudioFilePath: {
        required: requiredIf(() => formState.finalTextToAudio?.length === 0),
    },
    questions: { required, minLength: minLength(1) },
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<ConnectFlowSurveyFormState>>> = useVuelidate(
    rules,
    formState,
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchConnectFlowSurvey = async (id: number) => {
    try {
        await setLoading(true);
        const { data } = await ConnectFlowSurveyService.get<ConnectFlowSurveyModel>(id);

        formState.name = data.name;
        formState.textToAudio = data.textToAudio;
        formState.audioFilePath = data.audioFilePath;
        formState.questions = data.questions.map((question) => ({
            id: question.id,
            _key: `question_${question.id}`,
            label: question.label,
            rangeAnswer: question.rangeAnswer,
            textToAudio: question.textToAudio || null,
            audioFile: null,
            audioFilePath: question.audioFilePath || null,
        }));
        formState.isActive = data.isActive;
        formState.finalTextToAudio = data.finalTextToAudio;
        formState.finalAudioFilePath = data.finalAudioFilePath;
        await loadAudio();
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

const loadAudio = async () => {
    if (formState.audioFilePath) {
        try {
            const { data } = await VaultService.getAudioAsset(formState.audioFilePath);
            const blob = new Blob([data], { type: getAudioFormat(formState.audioFilePath) });
            file.value = new File([blob], formState.audioFilePath, {
                type: blob.type,
                lastModified: Date.now(),
            });
        } catch (e) {
            toastServiceError(e);
        }
    }

    if (formState.finalAudioFilePath) {
        try {
            const { data } = await VaultService.getAudioAsset(formState.finalAudioFilePath);
            const blob = new Blob([data], { type: getAudioFormat(formState.finalAudioFilePath) });
            finalFile.value = new File([blob], formState.finalAudioFilePath, {
                type: blob.type,
                lastModified: Date.now(),
            });
        } catch (e) {
            toastServiceError(e);
        }
    }
};

const onSubmit = async () => {
    const questionValidationResults = await Promise.all(
        Object.values(questionRefs.value).map((ref) => ref?.validate?.()),
    );

    const allQuestionsValid = questionValidationResults.every((valid) => valid);
    const isFormValid = await v$.value.$validate();

    if (!isFormValid || !allQuestionsValid) return;

    await setLoading(true);

    const formData = new FormData();
    formData.append('name', formState.name || '');
    formData.append('textToAudio', formState.textToAudio || '');
    if (file.value) formData.append('audio', file.value);
    formData.append('finalTextToAudio', formState.finalTextToAudio || '');
    if (finalFile.value) formData.append('finalAudio', finalFile.value);
    formData.append('isActive', formState.isActive.toString());

    formState.questions.forEach((question, index) => {
        if (question.id) formData.append(`questions[${index}][id]`, question.id.toString());
        formData.append(`questions[${index}][label]`, question.label || '');
        formData.append(`questions[${index}][rangeAnswer]`, question.rangeAnswer || '');
        formData.append(`questions[${index}][textToAudio]`, question.textToAudio || '');
        if (question.audioFile)
            formData.append(`questions[${index}][question-audio]`, question.audioFile);
        if (question.audioFilePath)
            formData.append(`questions[${index}][audioFilePath]`, question.audioFilePath || '');
    });

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewConnectFlowSurveyById', params: { id } });
    };

    try {
        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await ConnectFlowSurveyService.update(id, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await ConnectFlowSurveyService.create(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

const setQuestionRef = (el: Element | ComponentPublicInstance | null, key: string) => {
    if (el && '$el' in el) {
        questionRefs.value[key] = el as InstanceType<typeof ConnectFlowSurveyQuestionForm>;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // questionRefs.value[key] = null as any;
        delete questionRefs.value[key];
    }
};

const addQuestion = () => {
    formState.questions.push({
        _key: `tmp_${crypto.randomUUID()}`,
        label: null,
        rangeAnswer: null,
        textToAudio: null,
        audioFile: null,
        audioFilePath: null,
    });
};

const removeQuestion = (index: number) => {
    formState.questions.splice(index, 1);
};

watch([file], () => {
    if (file.value) {
        formState.audioFilePath = file.value.name;
        audioPreview.value = URL.createObjectURL(file.value);
        formState.textToAudio = null;
    } else {
        formState.audioFilePath = '';
        audioPreview.value = null;
    }
});

watch([finalFile], () => {
    if (finalFile.value) {
        formState.finalAudioFilePath = finalFile.value.name;
        finalAudioPreview.value = URL.createObjectURL(finalFile.value);
        formState.finalTextToAudio = null;
    } else {
        formState.finalAudioFilePath = '';
        finalAudioPreview.value = null;
    }
});

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        const surveyId = Number(route.params.id);
        if (!isNaN(surveyId)) fetchConnectFlowSurvey(surveyId);
    } else {
        addQuestion();
    }
});

const __returned__ = { props, route, uiStore, formState, file, audioPreview, questionRefs, finalFile, finalAudioPreview, rules, v$, setLoading, fetchConnectFlowSurvey, loadAudio, onSubmit, setQuestionRef, addQuestion, removeQuestion, get Permission() { return Permission }, get FormAction() { return FormAction }, get ConnectFlowSurveyQuestionForm() { return ConnectFlowSurveyQuestionForm } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})